<template>
  <div
    class="offer-detail tw-bg-white tw-relative tw-z-10"
  >
    <div class="tw-flex tw-flex-col">
      <offer-header
        :is-updated="isUpdated"
        :offer="offer"
        :proposal="proposal"
        data-test="header"
      />
      <offer-map-section
        v-if="hasMap"
        :offer="offer"
        data-test="map"
      />
      <offer-update
        :is-updated="isUpdated"
        v-if="isUpdated"
        :offer="offer"
        data-test="update"
      />
      <offer-price-section
        :offer="offer"
        :proposal="proposal"
        data-test="price"
      />
      <offer-directions-section
        :offer="offer"
        data-test="directions"
      />
      <offer-load-section
        :offer="offer"
        class="tw-mb-12 md:tw-mb-0"
        data-test="load"
      />
      <offer-specificity-section
        v-if="(hasVehiclesTypes && !isStandardVehicleSelection) || hasEquipment"
        :offer="offer"
        class="tw-w-full"
        data-test="vehicles"
      />
      <offer-booking-footer
        :offer="offer"
        :proposal="proposal"
        class="tw-w-full"
        data-test="footer"
      />
    </div>
  </div>
</template>

<script>
  import { defineComponent, onMounted, toRefs, watch, computed } from '@vue/composition-api'

  import OfferHeader from './components/OfferHeader/index.vue'
  import OfferUpdate from './components/OfferUpdate/index.vue'
  import OfferLoadSection from './components/OfferLoadSection/index.vue'
  import OfferDirectionsSection from './components/OfferDirectionsSection/index.vue'
  import OfferMapSection from './components/OfferMapSection/index.vue'
  import OfferPriceSection from './components/OfferPriceSection/index.vue'
  import OfferBookingFooter from './components/OfferBookingFooter/index.vue'
  import OfferSpecificitySection from './components/OfferSpecificitySection/index.vue'

  import useSticky from '@/composables/useSticky'
  import useRoute from '@/composables/useRoute'

  /**
   * Component regrouping the whole offer detail. This does not include the error states.
   * @module component - OfferDetail
   */
  export default defineComponent({
    name: 'OfferDetail',
    components: {
      OfferHeader,
      OfferUpdate,
      OfferLoadSection,
      OfferMapSection,
      OfferPriceSection,
      OfferDirectionsSection,
      OfferBookingFooter,
      OfferSpecificitySection
    },
    props: {
      offer: {
        type: Object,
        required: true
      },
      proposal: {
        type: Object,
        default: null
      },
      hasMap: {
        type: Boolean,
        default: true
      }
    },
    setup (props) {
      const { offer } = toRefs(props)
      const route = useRoute()

      const { create: updateBookingFooter } = useSticky('.offer-booking-footer', {
        verticalPosition: 'bottom'
      })

      const isStandardVehicleSelection = computed(() => {
        const vehicleTypes = offer.value.vehicle_types
        return vehicleTypes.length > 1 && vehicleTypes.some(vehicleType => vehicleType.key === 'semi_trailer')
      })

      const isUpdated = computed(() => {
        if (route.value.name !== 'Offer') {
          return false
        }
        const query = route.value.query
        const queryDate = query?.date
        const offerUpdatedAt = offer.value.offer_updated_at
        if (!queryDate || !offerUpdatedAt) {
          return false
        }
        return offerUpdatedAt > queryDate
      })

      const hasVehiclesTypes = computed(() => {
        return offer.value.vehicle_types && offer.value.vehicle_types.length > 0
      })

      const hasEquipment = computed(() => {
        return offer.value.equipments && offer.value.equipments.length > 0
      })

      watch(offer, (currentOffer, prevOffer) => {
        /**
         * Quick thingy to whenever the offer changes, scroll to the top of the
         * offer detail, but only relies on the uuid chnage.
         */
        if (prevOffer && prevOffer.uuid !== currentOffer.uuid) {
          const detail = document.querySelector('.offer-detail')
          if (!detail) return

          detail.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          })
        }
      }, { immediate: true })

      onMounted(() => {
        updateBookingFooter()
      })

      return {
        isStandardVehicleSelection,
        hasVehiclesTypes,
        hasEquipment,
        isUpdated
      }
    }
  })
</script>

<style lang="scss" scoped>@media only screen and (max-width: $breakpoint-tablet) {
  .offer-detail .offer-booking-footer {
    position: fixed !important;
  }
}
.offer-detail .offer-directions-section {
  position: relative;
}
.offer-detail .offer-directions-section::after {
  --tw-bg-opacity: 1;
  background-color: rgba(227, 227, 229, var(--tw-bg-opacity));
  margin-left: auto;
  margin-right: auto;
  position: absolute;
  content: '';
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  width: calc(100% - 5rem);
  height: 1px;
}</style>
